<template>
    <central-modal
        v-model:visible="isVisible"
        header="Plant Viewer"
        size="extra-large"
        @hide="emit('hide')"
    >
        <template #header>
            <div class="plant-page-header-options">
                <div class="selector">
                    <plant-selector
                        :selected-plant-ids="Object.keys(plantsInfo)"
                        placeholder-override="Compare plants"
                        @update:model-value-multiple="plants => addPlants(plants.map(plant => plant.id))"
                        multi-select
                        :multi-select-limit="10"
                    />
                    <info-icon-with-tooltip
                        tooltip-text="Select up to 10 plants to compare their details side by side"
                    />
                </div>
                <override-dashboard-dates
                    has-generic-charts
                    :date-overrides="explicitPlantDateOverride"
                    @update:current-dates-override="updateDashboardDatesOverride"
                />
                <prime-button
                    v-if="plantLength > 1"
                    class="plain-btn"
                    @click="openMapModal"
                >
                    View map
                    <font-awesome-icon :icon="['fas', 'map-location']" />
                </prime-button>
            </div>
        </template>
        <div class="plant-page-wrapper">
            <component-loader v-if="isViewLoading" />
            <div class="no-content" v-else-if="plantLength === 0">
                Please select a plant
            </div>
            <div
                v-else-if="plantLength === 1"
                class="scroll-container"
            >
                <plant-details
                    :plantId="firstPlantId"
                    :plant-details="plantsInfo[firstPlantId]?.plantInfo"
                    :related-plant-details="plantsInfo[firstPlantId]?.relatedEnactData"
                    :related-storetrack-details="plantsInfo[firstPlantId]?.relatedStoretrackDetails"
                    :hide-plant-details="props.hidePlantDetails"
                    :hide-table="props.hideTable"
                    :date-override="explicitPlantDateOverride"
                    :chart-date-range="plantChartDateRange"
                    :initial-details-tab="props.initialDetailsTab"
                />
            </div>
            <div
                v-else
                class="scroll-container"
            >
                <plant-compare-details
                    :plants="plantsInfo"
                    :date-override="explicitPlantDateOverride"
                    :chart-date-range="plantChartDateRange"
                />
            </div>
            <central-modal
                header="Map view"
                v-model:visible="showMapModal"
                size="medium"
            >
                <leaflet-map
                    v-if="plantMapData"
                    :zoom="5"
                    :hover="true"
                    :show-popup="false"
                    plantHasSize
                    :plantData="plantMapData"
                    :show-interconnectors="false"
                    :countryCode="'GB'"
                />
            </central-modal>
        </div>
    </central-modal>
</template>

<script setup lang="ts">
import {
    ref, watch, nextTick, computed,
} from 'vue';
import { useToast } from 'primevue/usetoast';
import { IPlantDetails, IEnactPlants, IPlantDetailsPlusRelated } from '@/interfaces/powerMarkets/plant/IPlantDetails.ts';
import { IDateRange } from '@/interfaces/general/IDateRange.ts';
import { ICsvData } from '@/interfaces/storageTracking/ICsvData.ts';
import PlantDetails from '@/components/powerMarkets/plant/PlantDetails.vue';
import PlantCompareDetails from '@/components/powerMarkets/plant/PlantCompareDetails.vue';
import OverrideDashboardDates from '@/components/powerMarkets/dashboard/OverrideDashboardDates.vue';
import PlantSelector from '@/components/powerMarkets/selectors/PlantSelector.vue';
import ComponentLoader from '@/components/layout/ComponentLoader.vue';
import { plantApi } from '@/api/powerMarkets/plantApi.ts';
import { DataApi } from '@/api/storageTracking/DataApi.ts';
import PrimeButton from 'primevue/button';
import CentralModal from '@/components/common/CentralModal.vue';
import LeafletMap from '@/components/powerMarkets/common/LeafletMap.vue';
import { useMultiPlantDetails } from '@/composables/powerMarkets/usePlantDetails.ts';
import InfoIconWithTooltip from '@/components/common/InfoIconWithTooltip.vue';
import type { PlantDetailsTab } from '@/constants/powerMarkets/plantDetailsTabs.ts';
import { usePlantDateOverride } from '@/composables/powerMarkets/usePlantDateOverride.ts';

const toast = useToast();

const props = withDefaults(
    defineProps<{
        visible: boolean;
        plantInitialIds: string[];
        fromDate?: Date;
        toDate?: Date;
        numberToCompare?: number;
        hidePlantDetails?: boolean;
        hideTable?: boolean;
        initialDetailsTab?: PlantDetailsTab;
    }>(),
    {
        numberToCompare: 1,
        fromDate: undefined,
        toDate: undefined,
        initialDetailsTab: undefined,
    },
);

const emit = defineEmits<{
    'update:visible': [boolean];
    'hide': [];
}>();

const plantsInfo = ref<Record<string, IPlantDetailsPlusRelated>>({});
const isViewLoading = ref<boolean>(false);
const showMapModal = ref<boolean>(false);
const isVisible = ref<boolean>(props.visible);

const { plantMapData } = useMultiPlantDetails(plantsInfo.value);

const firstPlantId = computed<string>(() => Object.keys(plantsInfo.value)[0]);
const plantLength = computed<number>(() => Object.keys(plantsInfo.value).length);

const {
    explicitPlantDateOverride,
    updateDashboardDatesOverride,
} = usePlantDateOverride();
const plantChartDateRange = computed<IDateRange | undefined>(() => (
    props.fromDate || props.toDate
        ? { from: props.fromDate ?? props.toDate!, to: props.toDate ?? props.fromDate! }
        : undefined
));

function openMapModal (): void {
    showMapModal.value = true;
}

function updateLoadState (value: boolean): void {
    isViewLoading.value = value;
}

function removePlant (plantId: string): void {
    delete plantsInfo.value[plantId];
}

async function addPlants (plantIds: string[]): Promise<void> {
    const configRequests: Promise<void>[] = [];
    plantIds.forEach(async (id) => {
        if (!plantsInfo.value[id]) configRequests.push(addPlant(id));
    });
    await Promise.all(configRequests);

    Object.keys(plantsInfo.value).forEach((plant) => {
        if (!plantIds.includes(plant)) removePlant(plant);
    });
}

async function addPlant (plantId: string): Promise<void> {
    try {
        const response = await plantApi.getAllRelatedPlants(plantId, 1);
        if (!response.data || response.data.plants.length === 0) return;
        const relatedEnactData : IEnactPlants = response.data;

        const plantInfo : IPlantDetails = relatedEnactData && relatedEnactData.plants.length === 1
            ? relatedEnactData.plants[0]
            : relatedEnactData.plants.find((plant) => plant.id === plantId) || relatedEnactData.plants[0] || null;

        const relatedStoretrackDetails: ICsvData | null = plantInfo?.storetrackId
            ? await DataApi.getProjectById(plantInfo.storetrackId)
            : null;

        plantsInfo.value[plantId] = {
            plantInfo,
            relatedEnactData,
            relatedStoretrackDetails,
        };
    } catch (error) {
        toast.add({
            summary: `Unable to retrieve all details for ${plantId}`,
            severity: 'error',
            detail: error,
            life: 10000,
        });
    }
}

watch(() => props.plantInitialIds, async () => {
    updateLoadState(true);
    await addPlants(props.plantInitialIds);
    nextTick(() => {
        updateLoadState(false);
    });
});

watch(() => props.visible, (newVal) => {
    isVisible.value = newVal;
});

watch(isVisible, (newVal) => {
    emit('update:visible', newVal);
});

async function initialise (): Promise<void> {
    updateLoadState(true);
    await addPlants(props.plantInitialIds);
    updateLoadState(false);
}

initialise();

</script>

<style lang="scss" scoped>
.plant-page-wrapper {
    height: 100%;
    overflow: auto;
}
.plant-page-header-options {
    display: flex;
    align-items: center;
    gap: var(--defaultSpacing);
    padding: 0 var(--largeSpacing);
    justify-self: end;

    .selector {
        display: flex;
        align-items: center;
    }

    button {
        flex-shrink: 0;
    }
}
.no-content {
    padding: var(--extraLargeSpacing);
}
.scroll-container {
    min-height: 100%;
}
</style>
